import { useHistory, useParams } from "react-router-dom";
import { useT } from "@transifex/react";
import AchievementBar from "../../../basic/AchievementBar";
import ExerciseNumberBadge from "../../../basic/exerciseNumberBadge/ExerciseNumberBadge";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { formatScoreWithoutTrailingZero } from "../../../helpers/ScoringHelper";
import { getIsScoreShown } from "../../../helpers/StudentResultScoreReveal";
import { getReachedPercentage, type StudentResultExercise } from "../../../helpers/StudentResultModel";
import { getStudentResultExerciseRoute } from "../../../helpers/navigationHelper";
import useBreakpoints from "../../../hooks/useBreakpoints";

interface StudentResultExerciseCardProps {
    exercise: StudentResultExercise;
}

/**
 * One exercise in the result list — the entry into its drill-in page. Not built on ExerciseCard:
 * the design puts the bar *under the description*, a slot that card has no room for, so this
 * composes the same pieces (number badge, bar, chevron) directly.
 *
 * The bar always shows; only its *colour* waits for the reveal, since a neutral bar states how much
 * was reached while a coloured one adds the verdict. The point numbers need a scored test too, or
 * they would only restate the right/wrong marks (see `docs/evaluation`).
 */
const StudentResultExerciseCard = (props: StudentResultExerciseCardProps) => {
    const history = useHistory();
    const { shareId } = useParams<{ shareId: string }>();
    const { isAboveMD } = useBreakpoints();

    const t = useT();
    const t_pointsAbbreviated = t("P.", { _tags: "StudentResult, ExerciseCard" });

    const handleOpen = () => {
        history.push(getStudentResultExerciseRoute(shareId, props.exercise.number));
    };

    const arePointsShown = getIsScoreShown(props.exercise.isScored);
    const reachedPercentage = getReachedPercentage(props.exercise.reachedScore, props.exercise.maxScore);

    const padding = isAboveMD ? "p-m" : "p-s";

    const renderDescription = (layout = "") => (
        <span className={`text-l text-p6-grey-dark break-words ${layout}`}>{props.exercise.description || "—"}</span>
    );

    /**
     * The bar is decorative and hidden from screen readers. While the points are gated it is the only
     * thing on the card that says how much was reached, so the figure is repeated as hidden text.
     */
    const renderBar = () => (
        <>
            <AchievementBar
                percentage={reachedPercentage}
                isColored={arePointsShown}
            />
            {!arePointsShown && (
                <span
                    className="sr-only"
                    data-testid="student-result-exercise-card-sr-percentage"
                >
                    {`${formatScoreWithoutTrailingZero(reachedPercentage)}%`}
                </span>
            )}
        </>
    );

    const renderPoints = (placement: string) => {
        if (!arePointsShown) return null;

        const reached = formatScoreWithoutTrailingZero(props.exercise.reachedScore);
        const max = formatScoreWithoutTrailingZero(props.exercise.maxScore);

        return (
            <span
                className={`shrink-0 text-right text-l text-p6-grey-dark whitespace-nowrap ${placement}`}
                data-testid="student-result-exercise-card-points"
            >
                {`${reached} / ${max} ${t_pointsAbbreviated}`}
            </span>
        );
    };

    // The design reserves a column for the points so the bars line up.
    const renderWideBody = () => (
        <div className="flex min-w-[0] flex-1 flex-row items-center gap-l">
            <div className="flex min-w-[0] flex-1 flex-row items-center gap-s">
                <ExerciseNumberBadge number={props.exercise.number} />
                <div
                    className="flex min-w-[0] flex-1 flex-col gap-xs"
                    data-testid="student-result-exercise-card-task-column"
                >
                    {renderDescription()}
                    {renderBar()}
                </div>
            </div>
            {renderPoints("min-w-[100px]")}
        </div>
    );

    // The points get a row of their own and hug the right edge.
    const renderNarrowBody = () => (
        <div className="flex min-w-[0] flex-1 flex-col gap-s">
            <div
                className="flex min-w-[0] flex-row items-start gap-s"
                data-testid="student-result-exercise-card-task-row"
            >
                <ExerciseNumberBadge number={props.exercise.number} />
                {renderDescription("min-w-[0] flex-1 line-clamp-2")}
            </div>
            {renderBar()}
            {renderPoints("self-end")}
        </div>
    );

    return (
        <button
            type="button"
            onClick={handleOpen}
            className={`flex w-full flex-row items-center gap-s border-none bg-white text-left cursor-pointer ${padding}`}
            data-testid="student-result-exercise-card"
        >
            {isAboveMD ? renderWideBody() : renderNarrowBody()}
            <PhaseSixIcon
                name="chevron-right"
                className="shrink-0 text-xxl text-p6-grey-dark"
            />
        </button>
    );
};

export default StudentResultExerciseCard;
